import ProTag from "@/app/components/ProTag";
import { Box, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";

interface Report {
  url: string;
  title: string;
  description: string;
  coverUrl: string;
  isFree: boolean;
}

interface ReportCardProps {
  report: Report;
  onReportClick: (url: string) => void;
}

export default function ReportCard({ report, onReportClick }: ReportCardProps) {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const [descriptionLines, setDescriptionLines] = useState(2);

  useEffect(() => {
    if (titleRef.current) {
      const titleElement = titleRef.current;
      const lineHeight = parseFloat(getComputedStyle(titleElement).lineHeight);
      const height = titleElement.scrollHeight;
      const actualLines = Math.round(height / lineHeight);
      
      // 根据标题实际占用行数计算描述可用行数
      const remainingLines = Math.max(1, 5 - actualLines);
      setDescriptionLines(remainingLines);
    }
  }, [report.title]);

  return (
    <Box
      sx={{
        flex: { xs: "1 1 100%", md: "0 0 calc(50% - 1.25rem)" },
        cursor: "pointer",
        flexDirection: "row",
        borderRadius: "0.75rem",
        backgroundColor: "background.paperMedium",
      }}
      onClick={() => onReportClick(report.url)}>
      <Box
        component="img"
        src={report.coverUrl}
        alt={report.title}
        sx={{
          height: "auto",
          objectFit: { sm: "cover", md: "contain" },
          borderRadius: 1,
        }}
      />
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: 3,
        }}>
        <Box sx={{ mb: 1.5 }}>
          <Typography
            ref={titleRef}
            variant="h5"
            component="h5"
            sx={{
              fontWeight: 600,
              fontSize: "var(--font-size-l)",
              lineHeight: 1.6,
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
              wordBreak: "break-word",
            }}>
            {report.isFree || (
              <ProTag
                sx={{ display: "inline-block", mr: 1, position: "relative", top: "-2px" }}
              />
            )}
            {report.title}
          </Typography>
        </Box>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            display: "-webkit-box",
            WebkitLineClamp: descriptionLines,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontSize: "var(--font-size-s)",
          }}>
          {report.description}
        </Typography>
      </Box>
    </Box>
  );
}
